import React, { useEffect, useState } from "react";
import logo from "./logo.svg";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import "./App.css";
import EventData from "./EventData";
import fetchBuilder from "fetch-retry-ts";
import {
  Alert,
  Box,
  CircularProgress,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import StripeContainer from "./StripeContainer";

interface EventProps {
  eventId: string;
  eventTitle: string;
}

const fetchRetry = fetchBuilder(fetch);

const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
});

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  //height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    backgroundAttachment: "fixed",
    ...theme.applyStyles("dark", {
      backgroundColor: "hsl(220, 30%, 5%)",
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

export default function Event(props: EventProps) {
  const [eventData, setEventData] = useState<EventData>();
  const [ticketsAvailable, setTicketsAvailable] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  document.title = `${props.eventTitle} tickets`;

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchRetry(`/api/event?id=${props.eventId}`);

      if (response.ok) {
        const data = await response.json();
        setEventData(data as EventData);
        setTicketsAvailable(data.maxTickets);
        setIsLoading(false);
        document.title = `${data.eventName} tickets`;
      }
    };

    fetchData();
  }, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline enableColorScheme />
      <SignUpContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography component="h3" variant="h6" sx={{ width: "100%" }}>
                Loading event...
              </Typography>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {eventData?.imageUrl && eventData.imageUrl.length > 0 ? (
                <img
                  src={`/eventImages/${eventData.imageUrl}`}
                  alt={eventData.eventName}
                  width={"100%"}
                />
              ) : (
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
                >
                  {eventData?.eventName}
                </Typography>
              )}
              <Typography component="h3" variant="h6" sx={{ width: "100%" }}>
                {eventData?.description}
              </Typography>
              {ticketsAvailable <= 0 ? (
                <Alert severity="warning">
                  There are no more tickets available.
                </Alert>
              ) : (
                <>
                  {eventData?.paymentMethod === "STRIPE" ? (
                    <StripeContainer event={eventData} />
                  ) : (
                    <></>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      </SignUpContainer>
    </ThemeProvider>
  );
}
